import React from 'react';
import { Link } from 'react-router-dom';
import '../HomePage.css';
import './BreakingNewsProblemPage.css';
import civitasLogo from '../assets/images/Civitas Signals-small.png';
import consiliumLogo from '../assets/images/Consilium-small.png';
import googlePlayImage from '../assets/images/google-play.png';
import appleStoreImage from '../assets/images/apple-store.png';
import { onAppStoreClick } from '../utils/metaPixel';

const PDF_PATH = '/breaking-news-problem-consilium.pdf';
const GOOGLE_PLAY_URL = process.env.REACT_APP_GOOGLE_PLAY_URL ?? 'https://play.google.com/store/apps/details?id=com.safescope.app&utm_source=na_Med';
const APP_STORE_URL = 'https://apps.apple.com/us/app/consilium-safety/id6760517718';

type NetworkHabit = {
  network: string;
  label: string;
  body: string;
  tone: 'quiet' | 'warning' | 'urgent';
};

const networkHabits: NetworkHabit[] = [
  {
    network: 'BBC News',
    label: 'Rare',
    body: 'Almost never used the phrase across two years of monitoring.',
    tone: 'quiet',
  },
  {
    network: 'Fox News',
    label: 'Occasional',
    body: 'Uses the label far less than its cable rivals.',
    tone: 'warning',
  },
  {
    network: 'MSNBC',
    label: 'Frequent',
    body: 'Among the heaviest users of the banner.',
    tone: 'urgent',
  },
  {
    network: 'CNN',
    label: 'Frequent, down',
    body: 'Ramped down after 2022, including after its CEO acknowledged the banner was being overused.',
    tone: 'urgent',
  },
];

const trustData = [
  { label: 'Neutral framing', value: '3.66', width: '52.3%', tone: 'neutral' },
  { label: '"Breaking news" framing', value: '2.92', width: '41.7%', tone: 'red' },
];

const sensationData = [
  { label: 'Neutral framing', value: '4.01', width: '57.3%', tone: 'neutral' },
  { label: '"Breaking news" framing', value: '4.78', width: '68.3%', tone: 'red' },
];

const BreakingNewsProblemPage: React.FC = () => (
  <div className="breaking-news-page-wrapper">
    <nav className="navbar breaking-news-navbar">
      <div className="logo">
        <Link to="/" aria-label="Consilium home">
          <img src={consiliumLogo} alt="Consilium" className="logo-image logo-image-consilium" />
        </Link>
      </div>
      <ul className="nav-links breaking-news-nav-links">
        <li><a href="/privacy.html">Privacy Policy</a></li>
        <li><a href="/account-deletion.html">Account Deletion</a></li>
        <li><Link to="/breaking-news-problem">Breaking News Problem</Link></li>
        <li><Link to="/public-safety-data">Safety Data Guide</Link></li>
        <li><Link to="/health-environmental-data">Health &amp; Environment</Link></li>
        <li><Link to="/faq">FAQ</Link></li>
        <li><a href="#download-consilium" className="nav-cta">Download</a></li>
      </ul>
    </nav>

    <main className="breaking-news-page" id="top">
      <header className="breaking-news-hero">
        <div className="breaking-news-alert-row">
          <span className="breaking-news-dot" aria-hidden="true" />
          <span>Media Analysis</span>
          <span>On The Overuse Of "Breaking News"</span>
        </div>
        <div className="breaking-news-hero-grid">
          <div>
            <div className="breaking-news-label">Breaking News</div>
            <h1>When everything is breaking, nothing is.</h1>
            <p>
              News outlets use urgent banners for hurricanes, hearings, old clips, and stories that have been running
              for hours. The result is not clarity. It is a constant pressure signal that makes everyday information
              feel like an emergency.
            </p>
            <div className="breaking-news-actions">
              <a href={PDF_PATH} className="breaking-news-primary-link" download>
                Download the hardcopy PDF
              </a>
              <a href="#consilium-response" className="breaking-news-secondary-link">
                See how Consilium handles it
              </a>
            </div>
          </div>
          <aside className="breaking-news-hero-card" aria-label="Consilium summary">
            <img src={consiliumLogo} alt="Consilium" />
            <p>
              Consilium is built to strip away fear, urgency theater, and recycled headlines, then show you the facts
              that actually matter to your safety.
            </p>
          </aside>
        </div>
      </header>

      <section className="breaking-news-callout">
        <div className="breaking-news-eyebrow">The Pattern You Have Probably Noticed</div>
        <p>
          Turn on cable news long enough and the red bar comes back, sometimes for the same story after every commercial
          break. Nobody publishes a clean count of repeated banners, but research does show that networks use the same
          words with wildly different habits.
        </p>
      </section>

      <section className="breaking-news-quote" aria-label="Industry admission">
        <div className="breaking-news-eyebrow">The Industry Said It First</div>
        <blockquote>
          "Something I have heard from both people inside and outside the organization is complaints we overuse the
          'Breaking News' banner. I agree."
        </blockquote>
        <cite>Chris Licht, then-CEO of CNN, internal staff memo reported in June 2022</cite>
      </section>

      <section className="breaking-news-section">
        <div className="breaking-news-eyebrow">Data - GDELT Project / Internet Archive TV News Archive</div>
        <h2>Four networks. One label. Very different habits.</h2>
        <p>
          Researchers tracked on-screen text across BBC News, CNN, MSNBC, and Fox News since January 2020, measuring
          how many seconds of airtime carried the words "breaking news" anywhere on screen.
        </p>
        <div className="breaking-news-network-list">
          {networkHabits.map(({ network, label, body, tone }) => (
            <article className="breaking-news-network-card" key={network}>
              <h3>{network}</h3>
              <span className={`breaking-news-pill breaking-news-pill-${tone}`}>{label}</span>
              <p>{body}</p>
            </article>
          ))}
        </div>
        <div className="breaking-news-note">
          <strong>Also cyclical:</strong> usage spikes on weekdays and drops sharply on weekends. Actual emergencies do
          not follow a five-day work week. Newsroom staffing and programming choices do.
        </div>
      </section>

      <section className="breaking-news-section breaking-news-stats-section">
        <div className="breaking-news-eyebrow">Data - Media Research Center, August 2021</div>
        <h2>Only about a fifth of cable "news" is hard news.</h2>
        <p>
          The Media Research Center coded 108 hours of CNN, Fox News, and MSNBC programming from February through June
          2021. Just 22.3% was hard news, including field reports, live breaking coverage, and brief host-read items.
          Everything else was discussion, interviews, and opinion.
        </p>

        <div className="breaking-news-share-bar" aria-label="Hard news share">
          <span className="breaking-news-share-hard">22.3% hard news</span>
          <span className="breaking-news-share-other">77.7% discussion, opinion, interviews</span>
        </div>

        <div className="breaking-news-stat-grid">
          <article>
            <span className="breaking-news-big-stat">9:15</span>
            <p>average hard news per hour</p>
          </article>
          <article>
            <span className="breaking-news-big-stat breaking-news-muted-stat">18:00</span>
            <p>average commercials per hour</p>
          </article>
        </div>
      </section>

      <section className="breaking-news-section">
        <div className="breaking-news-eyebrow">Data - Electronics, 2023</div>
        <h2>The label itself makes people trust it less.</h2>
        <p>
          Researchers showed people the same story two ways: framed neutrally and framed as "breaking news." The
          breaking-news version was rated more sensational and less trustworthy. Both differences were statistically
          significant.
        </p>

        <div className="breaking-news-bars">
          <h3>Trust score, 1-7 scale</h3>
          {trustData.map(({ label, value, width, tone }) => (
            <div className="breaking-news-bar-row" key={label}>
              <span>{label}</span>
              <div className="breaking-news-bar-track">
                <div className={`breaking-news-bar-fill breaking-news-bar-${tone}`} style={{ width }} />
              </div>
              <strong>{value}</strong>
            </div>
          ))}
        </div>

        <div className="breaking-news-bars">
          <h3>Perceived sensationalism, 1-7 scale</h3>
          {sensationData.map(({ label, value, width, tone }) => (
            <div className="breaking-news-bar-row" key={label}>
              <span>{label}</span>
              <div className="breaking-news-bar-track">
                <div className={`breaking-news-bar-fill breaking-news-bar-${tone}`} style={{ width }} />
              </div>
              <strong>{value}</strong>
            </div>
          ))}
        </div>
      </section>

      <section className="breaking-news-consilium" id="consilium-response">
        <div>
          <div className="breaking-news-eyebrow">The Consilium Response</div>
          <h2>Facts first. Urgency only when it is earned.</h2>
          <p>
            Consilium is designed for people who need to know what is real, nearby, verified, and actionable. It removes
            stale stories, emotional framing, and attention-driven urgency so your alerts can stay calm and useful.
          </p>
        </div>
        <ul>
          <li>Verified information from monitored sources, not recycled headlines.</li>
          <li>Local relevance, so broad national noise does not crowd out what affects you.</li>
          <li>Plain-language alerts that separate what happened, where it happened, and what to do next.</li>
        </ul>
      </section>

      <section className="breaking-news-app-cta" id="download-consilium">
        <div>
          <div className="breaking-news-eyebrow">Download Consilium</div>
          <h2>Get verified local intelligence without the noise.</h2>
          <p>
            Install Consilium to receive calm, relevant safety alerts for the places you care about. No sensationalized
            headlines. No recycled urgency. Just what is confirmed and useful.
          </p>
        </div>
        <div className="store-buttons breaking-news-store-buttons" aria-label="Download Consilium">
          <a
            href={GOOGLE_PLAY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="store-button google"
            aria-label="Get Consilium on Google Play"
            onClick={onAppStoreClick('android', { placement: 'breaking-news-page' })}
          >
            <img src={googlePlayImage} alt="Get it on Google Play" className="store-icon" />
          </a>
          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="store-button apple"
            aria-label="Download Consilium on the App Store"
            onClick={onAppStoreClick('ios', { placement: 'breaking-news-page' })}
          >
            <img src={appleStoreImage} alt="Download on the App Store" className="store-icon" />
          </a>
        </div>
      </section>

      <section className="breaking-news-download-card">
        <h2>Want the hardcopy?</h2>
        <p>Download the formatted PDF version of this analysis for offline reading or sharing.</p>
        <a href={PDF_PATH} className="breaking-news-primary-link" download>
          Download PDF
        </a>
      </section>

      <section className="breaking-news-companion-card">
        <h2>Want to check the data behind a local safety claim?</h2>
        <p>
          Read the companion guide on baselines, sources, reporting lag, and rumor chains:
          <Link to="/public-safety-data"> How to Read Your Neighborhood's Public Safety Data</Link>, plus the
          <Link to="/health-environmental-data"> health and environmental data guide</Link>.
        </p>
      </section>

      <section className="breaking-news-sources">
        <h2>Sources</h2>
        <p>
          GDELT Project, "Tracking Breaking News Chyrons on Television News"; Media Research Center, "Actual News Is
          Hard To Find On Today's Cable 'News'" (Aug. 2021); Pelau, Pop, Stanescu and Sanda, "The Breaking News Effect,"
          Electronics 12(2):423 (2023); Chris Licht internal memo as reported by NewscastStudio (Jun. 2022).
        </p>
      </section>
    </main>

    <footer className="footer breaking-news-footer">
      <div className="footer-content">
        <div className="footer-section">
          <img src={civitasLogo} alt="Civitas Signals" className="footer-logo" />
          <p>Protecting families with real-time local intelligence.</p>
          <div className="footer-links breaking-news-footer-links">
            <Link to="/">Home</Link>
            <Link to="/faq">FAQ</Link>
            <Link to="/public-safety-data">Safety Data Guide</Link>
            <Link to="/health-environmental-data">Health &amp; Environment</Link>
            <a href="/privacy.html">Privacy Policy</a>
            <a href={PDF_PATH} download>Download PDF</a>
          </div>
        </div>
      </div>
      <div className="footer-bottom">
        <p>&copy; {new Date().getFullYear()} Civitas Signals, Inc. All rights reserved.</p>
      </div>
    </footer>
  </div>
);

export default BreakingNewsProblemPage;
